import { useCallback, useEffect, useRef, useState } from 'react';
import { admitTicket, doorGuest, nightOf, normalizeTicketCode, pendingDoorPeople, pendingTickets, ticketCheck, type LicenceStatus, type Ticket } from '@makila/pos-core';
import { Beer, CheckCircle2, LayoutDashboard, Minus, Moon, Plug, Plus, RefreshCw, ScanLine, ShoppingCart, Sun, Ticket as TicketIcon, Undo2, UserPlus, Wallet, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { HardwareDialog } from '../components/HardwareDialog';
import { Rail, RailItem, RailTool } from '../components/Rail';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, identity, terminalApi } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { SyncStatus } from '../lib/sync';
import { useTheme } from '../lib/theme';
import type { Operator } from './Lock';

interface Props {
  store: IdbTerminalStore; device: DeviceState; licence: LicenceStatus | null; sync: SyncStatus | null; operator: Operator;
  onCounter: (open?: 'cash' | 'return') => void; onTables?: () => void; onLock: () => void; onSyncNow: () => void; onGestao?: () => void;
}
interface Cloud { night: string; sale: number; ticket: number; guest: number }
type Seen = { at: string; ok: boolean; text: string };
const dmy = (d: string) => d.split('-').reverse().join('/');
const people = (n: number) => (n === 1 ? '1 pessoa' : `${n} pessoas`);

/**
 * Porta do bar ou da discoteca (docs/14 Fase 2): lê o QR do bilhete (o leitor
 * escreve o código e carrega Enter) e deixa entrar logo se for válido — sem
 * rede também, com os bilhetes do catálogo. Regista convidados e mostra quantas
 * pessoas entraram esta noite (todas as caixas, mais o que esta ainda não enviou).
 */
export function DoorScreen(p: Props) {
  const [tickets, setTickets] = useState<Map<string, Ticket>>(new Map());
  const [cloud, setCloud] = useState<Cloud | null>(null);
  const [pending, setPending] = useState(0);
  const [q, setQ] = useState('');
  const [result, setResult] = useState<{ ok: boolean; title: string; detail: string } | null>(null);
  const [log, setLog] = useState<Seen[]>([]);
  const [guests, setGuests] = useState<{ n: number; note: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hwOpen, setHwOpen] = useState(false);
  const [dark, toggleTheme] = useTheme();
  // Lidos nesta caixa e ainda não confirmados pelo catálogo: não entram duas vezes.
  const usedHere = useRef(new Set<string>());
  const input = useRef<HTMLInputElement>(null);
  const night = nightOf();

  const refresh = useCallback(async () => {
    const out = await p.store.pendingOutbox(5000);
    setPending(pendingDoorPeople(out, nightOf()));
    // Os do catálogo mandam (usado, anulado); os vendidos aqui e ainda por enviar também valem.
    const cat = (await p.store.catalog('ticket')).map((i) => i.data as unknown as Ticket);
    setTickets(new Map([...pendingTickets(out), ...cat].map((t) => [t.code, t])));
    try { setCloud(await terminalApi<Cloud>(p.device, '/sync/door')); } catch { /* sem rede: fica o último */ }
  }, [p.store, p.device]);

  useEffect(() => { void refresh(); }, [p.sync?.lastSyncAt, refresh]);

  const total = (cloud && cloud.night === night ? cloud.sale + cloud.ticket + cloud.guest : 0) + pending;
  const note = (s: Seen) => setLog((l) => [s, ...l].slice(0, 40));
  const now = () => new Date().toTimeString().slice(0, 5);

  const scan = async () => {
    const code = normalizeTicketCode(q);
    setQ('');
    input.current?.focus();
    if (!code) { setResult({ ok: false, title: 'Código inválido', detail: 'O código do bilhete tem 8 letras e algarismos (ABCD-2345)' }); return; }
    const t = tickets.get(code);
    const c = ticketCheck(t, new Date(), usedHere.current);
    if (!c.ok || !t) {
      setResult({ ok: false, title: c.reason ?? 'Bilhete inválido', detail: code });
      note({ at: now(), ok: false, text: `${code} · ${c.reason}` });
      return;
    }
    try {
      await admitTicket(p.store, identity(p.device), { code, admits: t.admits, operatorId: p.operator.id });
      usedHere.current.add(code);
      setResult({ ok: true, title: `Pode entrar · ${people(t.admits)}`, detail: `${t.name} · ${code}` });
      note({ at: now(), ok: true, text: `${t.name} · ${people(t.admits)}` });
      p.onSyncNow();
      void refresh();
    } catch (e) { setError((e as Error).message); }
  };

  const addGuests = async () => {
    if (!guests) return;
    try {
      await doorGuest(p.store, identity(p.device), { people: guests.n, note: guests.note, operatorId: p.operator.id });
      note({ at: now(), ok: true, text: `Convidados (${guests.note.trim()}) · ${people(guests.n)}` });
      setGuests(null); setError(null);
      p.onSyncNow();
      void refresh();
      input.current?.focus();
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="flex h-full flex-col-reverse md:flex-row">
      <Rail label="Caixa" footer={<>
        {p.onGestao && <RailItem icon={LayoutDashboard} label="Gestão" kbd="F10" ariaLabel="Abrir a gestão" onClick={p.onGestao} />}
        <RailTool icon={Plug} label="Equipamento" onClick={() => setHwOpen(true)} />
        <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
        <RailTool icon={RefreshCw} label="Sincronizar agora" onClick={p.onSyncNow} />
        <RailTool label="Trocar de operador" onClick={p.onLock}>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-700 text-sm font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
        </RailTool>
      </>}>
        {p.onTables && <RailItem icon={Beer} label="Contas" onClick={p.onTables} />}
        <RailItem icon={TicketIcon} label="Porta" active />
        <RailItem icon={ShoppingCart} label="Balcão" onClick={() => p.onCounter()} />
        <RailItem icon={Undo2} label="Devolução" kbd="F8" disabled={!p.licence?.sell} onClick={() => p.onCounter('return')} />
        <RailItem icon={Wallet} label="Caixa" kbd="F9" onClick={() => p.onCounter('cash')} />
      </Rail>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
          <div className="min-w-0">
            <div className="truncate font-display text-[17px] font-semibold leading-tight">Porta</div>
            <div className="truncate text-xs text-muted">Noite de {dmy(night)} · {p.operator.name}</div>
          </div>
          <div className="ml-auto"><StatusPill sync={p.sync} /></div>
        </header>
        {error && <div role="alert" className="bg-amber-100 px-4 py-2 text-amber-900">{error}</div>}
        <main className="grid min-h-0 flex-1 gap-6 overflow-y-auto p-4 lg:grid-cols-[1fr_320px] lg:p-6">
          <section className="mx-auto w-full max-w-2xl space-y-4">
            <div className="surface flex items-center gap-4 rounded-2xl p-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-muted">Entraram esta noite</div>
                <div aria-label="Pessoas esta noite" className="num font-display text-5xl font-bold leading-tight">{total}</div>
                <div className="text-xs text-muted">
                  {cloud && cloud.night === night ? `${cloud.sale} à porta · ${cloud.ticket} com bilhete · ${cloud.guest} convidados` : 'Sem ligação: só o desta caixa'}
                  {pending > 0 && ` · ${pending} por enviar`}
                </div>
              </div>
              <button onClick={() => p.onCounter()} className="flex h-14 items-center gap-2 rounded-2xl bg-brand-700 px-4 font-semibold text-white hover:bg-brand-800"><ShoppingCart size={20} /> Vender entrada</button>
            </div>

            <div className="relative">
              <ScanLine className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={22} />
              <input ref={input} autoFocus aria-label="Código do bilhete" placeholder="Ler o QR ou escrever o código" value={q} autoComplete="off" autoCapitalize="characters"
                onChange={(e) => { setQ(e.target.value); setResult(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) { e.preventDefault(); void scan(); } }}
                className="h-16 w-full rounded-2xl border border-base bg-[var(--surface)] pl-14 pr-4 text-xl uppercase tracking-wider shadow-sm outline-none placeholder:normal-case placeholder:tracking-normal focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10" />
            </div>

            {result && (
              <div role="status" aria-label={result.ok ? 'Bilhete válido' : 'Bilhete recusado'} className={clsx('flex items-center gap-4 rounded-3xl p-6 text-white shadow-lg', result.ok ? 'bg-green-700' : 'bg-brand-700')}>
                {result.ok ? <CheckCircle2 size={56} /> : <XCircle size={56} />}
                <div className="min-w-0">
                  <div className="font-display text-2xl font-bold sm:text-3xl">{result.title}</div>
                  <div className="mt-1 truncate text-lg">{result.detail}</div>
                </div>
              </div>
            )}

            {!guests ? (
              <button onClick={() => setGuests({ n: 1, note: '' })} className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl border border-base font-semibold hover:bg-surface-2"><UserPlus size={20} /> Convidados (entram sem pagar)</button>
            ) : (
              <div className="surface space-y-3 rounded-2xl p-4">
                <h2 className="font-display font-semibold">Convidados</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <button aria-label="Menos" onClick={() => setGuests({ ...guests, n: Math.max(1, guests.n - 1) })} className="grid h-12 w-12 place-items-center rounded-xl border border-base"><Minus size={20} /></button>
                  <span aria-label="Número de convidados" className="num w-16 text-center text-3xl font-bold">{guests.n}</span>
                  <button aria-label="Mais" onClick={() => setGuests({ ...guests, n: Math.min(50, guests.n + 1) })} className="grid h-12 w-12 place-items-center rounded-xl border border-base"><Plus size={20} /></button>
                  <input aria-label="De quem são os convidados" placeholder="De quem? (ex.: lista do DJ)" value={guests.note} maxLength={200}
                    onChange={(e) => setGuests({ ...guests, note: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') void addGuests(); }}
                    className="h-12 min-w-0 flex-1 rounded-xl border border-base bg-[var(--surface)] px-3" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setGuests(null)} className="h-12 rounded-xl border border-base font-medium">Cancelar</button>
                  <button onClick={() => void addGuests()} disabled={!guests.note.trim()} className="h-12 rounded-xl bg-brand-700 font-semibold text-white disabled:opacity-40">Registar entrada</button>
                </div>
              </div>
            )}
          </section>
          <aside className="surface h-fit rounded-2xl p-4">
            <h2 className="mb-2 font-display font-semibold">Nesta porta</h2>
            {!log.length ? <p className="text-sm text-muted">Ainda ninguém passou por esta caixa.</p> : (
              <ul className="space-y-1.5 text-sm">
                {log.map((l, i) => <li key={i} className="flex items-center gap-2"><span className={clsx('h-2.5 w-2.5 shrink-0 rounded-full', l.ok ? 'bg-green-600' : 'bg-brand-700')} /><span className="num text-muted">{l.at}</span><span className="flex-1 truncate">{l.text}</span></li>)}
              </ul>
            )}
          </aside>
        </main>
      </div>
      {hwOpen && <HardwareDialog onClose={() => setHwOpen(false)} />}
    </div>
  );
}
